/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import {
  ListManagedDevicesResult,
  ListManagedDevicesResult$inboundSchema,
} from "../components/listmanageddevicesresult.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListManagedDevicesSecurity = {
  apikeyHeaderGramKey?: string | undefined;
  sessionHeaderGramSession?: string | undefined;
};

/**
 * Only devices in this coverage bucket.
 */
export const QueryParamCoverageBucket = {
  AgentActive: "agent_active",
  AgentStale: "agent_stale",
  AgentOtherDevice: "agent_other_device",
  NoAgent: "no_agent",
  NoEmail: "no_email",
  UnresolvedEmail: "unresolved_email",
  Missing: "missing",
} as const;
/**
 * Only devices in this coverage bucket.
 */
export type QueryParamCoverageBucket = ClosedEnum<
  typeof QueryParamCoverageBucket
>;

/**
 * Only agent_active devices in this policy state.
 */
export const QueryParamPolicyState = {
  Attested: "attested",
  Outdated: "outdated",
  Unreported: "unreported",
  Halted: "halted",
} as const;
/**
 * Only agent_active devices in this policy state.
 */
export type QueryParamPolicyState = ClosedEnum<typeof QueryParamPolicyState>;

export type ListManagedDevicesRequest = {
  /**
   * Only devices synced from this provider.
   */
  provider?: string | undefined;
  /**
   * Only devices in this coverage bucket.
   */
  coverageBucket?: QueryParamCoverageBucket | undefined;
  /**
   * Only agent_active devices in this policy state.
   */
  policyState?: QueryParamPolicyState | undefined;
  /**
   * Only devices assigned to these Speakeasy users. Combined with user_emails as an OR, because a device only carries a resolved user id when the MDM's reported email matched a member.
   */
  userIds?: Array<string> | undefined;
  /**
   * Only devices whose MDM-reported assigned email is one of these, matched case-insensitively.
   */
  userEmails?: Array<string> | undefined;
  /**
   * Pagination cursor from a previous page.
   */
  cursor?: string | undefined;
  /**
   * Page size. Defaults to 50, maximum 200.
   */
  limit?: number | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
};

export type ListManagedDevicesResponse = {
  result: ListManagedDevicesResult;
};

/** @internal */
export type ListManagedDevicesSecurity$Outbound = {
  "apikey_header_Gram-Key"?: string | undefined;
  "session_header_Gram-Session"?: string | undefined;
};

/** @internal */
export const ListManagedDevicesSecurity$outboundSchema: z.ZodMiniType<
  ListManagedDevicesSecurity$Outbound,
  ListManagedDevicesSecurity
> = z.pipe(
  z.object({
    apikeyHeaderGramKey: z.optional(z.string()),
    sessionHeaderGramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function listManagedDevicesSecurityToJSON(
  listManagedDevicesSecurity: ListManagedDevicesSecurity,
): string {
  return JSON.stringify(
    ListManagedDevicesSecurity$outboundSchema.parse(listManagedDevicesSecurity),
  );
}

/** @internal */
export const QueryParamCoverageBucket$outboundSchema: z.ZodMiniEnum<
  typeof QueryParamCoverageBucket
> = z.enum(QueryParamCoverageBucket);

/** @internal */
export const QueryParamPolicyState$outboundSchema: z.ZodMiniEnum<
  typeof QueryParamPolicyState
> = z.enum(QueryParamPolicyState);

/** @internal */
export type ListManagedDevicesRequest$Outbound = {
  provider?: string | undefined;
  coverage_bucket?: string | undefined;
  policy_state?: string | undefined;
  user_ids?: Array<string> | undefined;
  user_emails?: Array<string> | undefined;
  cursor?: string | undefined;
  limit: number;
  "Gram-Key"?: string | undefined;
  "Gram-Session"?: string | undefined;
};

/** @internal */
export const ListManagedDevicesRequest$outboundSchema: z.ZodMiniType<
  ListManagedDevicesRequest$Outbound,
  ListManagedDevicesRequest
> = z.pipe(
  z.object({
    provider: z.optional(z.string()),
    coverageBucket: z.optional(QueryParamCoverageBucket$outboundSchema),
    policyState: z.optional(QueryParamPolicyState$outboundSchema),
    userIds: z.optional(z.array(z.string())),
    userEmails: z.optional(z.array(z.string())),
    cursor: z.optional(z.string()),
    limit: z._default(z.int(), 50),
    gramKey: z.optional(z.string()),
    gramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      coverageBucket: "coverage_bucket",
      policyState: "policy_state",
      userIds: "user_ids",
      userEmails: "user_emails",
      gramKey: "Gram-Key",
      gramSession: "Gram-Session",
    });
  }),
);

export function listManagedDevicesRequestToJSON(
  listManagedDevicesRequest: ListManagedDevicesRequest,
): string {
  return JSON.stringify(
    ListManagedDevicesRequest$outboundSchema.parse(listManagedDevicesRequest),
  );
}

/** @internal */
export const ListManagedDevicesResponse$inboundSchema: z.ZodMiniType<
  ListManagedDevicesResponse,
  unknown
> = z.pipe(
  z.object({
    Result: ListManagedDevicesResult$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "Result": "result",
    });
  }),
);

export function listManagedDevicesResponseFromJSON(
  jsonString: string,
): SafeParseResult<ListManagedDevicesResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListManagedDevicesResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListManagedDevicesResponse' from JSON`,
  );
}
